import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PayslipDocument from "./PayslipDocument";
import { PayslipData } from "../../types/payslip";

interface PayslipPreviewModalProps {
  open: boolean;
  onClose: () => void;
  data: PayslipData;
}

export const PayslipPreviewModal: React.FC<PayslipPreviewModalProps> = ({ open, onClose, data }) => {
  const handlePrint = () => {
    const printContent = document.getElementById("printable-payslip-modal");
    if (!printContent) return;

    // Collect all loaded CSS rules and styles
    let allCssRules = "";
    try {
      Array.from(document.styleSheets).forEach((sheet) => {
        try {
          if (sheet.cssRules) {
            Array.from(sheet.cssRules).forEach((rule) => {
              allCssRules += rule.cssText + "\n";
            });
          }
        } catch {
          // Ignore cross-origin stylesheet errors
        }
      });
    } catch {
      // Fallback
    }

    const employeeName = data.employee.name ? data.employee.name.replace(/[^a-zA-Z0-9]/g, "_") : "Employee";
    const title = `Payslip_${employeeName}_${data.payMonth}_${data.payYear}`;

    // Create a hidden iframe for seamless, high-fidelity printing without popup blocker issues
    let iframe = document.getElementById("payslip-print-frame") as HTMLIFrameElement;
    if (iframe) {
      document.body.removeChild(iframe);
    }
    iframe = document.createElement("iframe");
    iframe.id = "payslip-print-frame";
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "none";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow?.document;
    if (!doc) return;

    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${title}</title>
          <link rel="preconnect" href="https://fonts.googleapis.com">
          <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
          <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
          <style>
            ${allCssRules}
          </style>
          <style>
            @page {
              size: A4 portrait;
              margin: 8mm 10mm;
            }
            * {
              box-sizing: border-box !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background-color: #ffffff !important;
              color: #1e293b !important;
              font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
              width: 100% !important;
            }
            .print-container {
              width: 100% !important;
              max-width: 800px !important;
              margin: 0 auto !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            #printable-payslip-modal {
              box-shadow: none !important;
              border: 1px solid #e2e8f0 !important;
              max-width: 100% !important;
              width: 100% !important;
              padding: 24px !important;
              margin: 0 !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printContent.outerHTML}
          </div>
        </body>
      </html>
    `);
    doc.close();

    // Trigger print once styles and fonts are ready
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    }, 300);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: "#f8fafc",
          maxHeight: "92vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e2e8f0",
          bgcolor: "#ffffff",
          py: 2,
          px: 3,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
            Payslip Preview
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            {data.employee.name ? `${data.employee.name} • ` : ""}
            {data.payMonth} {data.payYear}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: "#f1f5f9" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <PayslipDocument data={data} id="printable-payslip-modal" />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #e2e8f0",
          bgcolor: "#ffffff",
          py: 2,
          px: 3,
        }}
      >
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close & Edit
        </Button>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            onClick={handlePrint}
            variant="outlined"
            color="primary"
            startIcon={<PrintIcon />}
          >
            Print
          </Button>
          <Button
            onClick={handlePrint}
            variant="contained"
            color="primary"
            startIcon={<PictureAsPdfIcon />}
          >
            Download PDF
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PayslipPreviewModal;
